import { FileButton } from '@e3dc-react/shell/Elements/Buttons'
import { Grid, useMediaQuery, useTheme } from '@mui/material'
import React, { useCallback, useState } from 'react'
import useInstallationFiles from '../hooks/use-installation-files'
import useModals from 'src/Hooks/useModals'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { E3more } from '@e3dc-react/icons'
import { IMAGES_EXTENSION_TYPES } from '../consts'
import { MenuItemModel } from '@e3dc-react/shell/Elements/Buttons/FileButton'
import { useDebouncedCallback } from 'use-debounce'
import { MimeType } from '@e3dc-react/shell/Types'
import { FileWithLatestStatus } from 'src/Libs/installation-statement/models'
import useIsWebviewMode from 'src/Hooks/use-is-webview-mode'

const DOWNLOAD_DEBOUNCE_TIME_IN_MS = 200
const DEFAULT_MIME_TYPE: MimeType = 'application/pdf'

interface Props {
  file: FileWithLatestStatus
  installationId: string | undefined
  /** setImageOpened is used to prevent opening two images at the same time */
  setImageOpened: (fileId: number, value: boolean) => void
  disableOpenImage: boolean
  fileClassName?: string
  fileContentClassName?: string
}

/**
 * Component to display an installation statement document file with functionality to download it
 */
const DocumentFile: React.FC<Props> = ({ file, installationId, setImageOpened, disableOpenImage, fileClassName, fileContentClassName }) => {
  const { downloadFile, previewFile } = useInstallationFiles()
  const { openPreviewImage } = useModals()
  const { t } = useCustomTranslation()
  const hasMenuActions = IMAGES_EXTENSION_TYPES.includes(file.extension ?? '')
  const [loading, setLoading] = useState(false)
  const { breakpoints } = useTheme()
  const isBigScreen = useMediaQuery(breakpoints.up('sm'))
  const isWebview = useIsWebviewMode()

  /* TODO: when BE provides thumbnails show a preview of the image */
  const handleImageSrc = useCallback(
    async (file: FileWithLatestStatus, installationId?: number): Promise<void> => {
      if (!installationId) return
      setImageOpened(file.id, true)
      const imageUrl = await previewFile(installationId, file)
      openPreviewImage({ url: imageUrl, file, setImagePreviewOpened: (value: boolean) => setImageOpened(file.id, value) })
    },
    [openPreviewImage, previewFile, setImageOpened]
  )

  const handleOnClickFileDownload = useCallback(
    (file: FileWithLatestStatus) => {
      setLoading(true)
      if (installationId && file) {
        downloadFile(Number(installationId), file).finally(() => setLoading(false))
      }
    },
    [installationId, downloadFile]
  )

  const handleOnClickDisplayImage = useCallback(
    (file: FileWithLatestStatus) => {
      if (installationId && file && !disableOpenImage) {
        setLoading(true)
        handleImageSrc(file, Number(installationId)).finally(() => setLoading(false))
      }
    },
    [installationId, handleImageSrc, disableOpenImage]
  )

  const debouncedDownload = useDebouncedCallback(file => handleOnClickFileDownload(file), DOWNLOAD_DEBOUNCE_TIME_IN_MS)

  const handleOnClickDownload = useCallback(() => {
    if (loading) {
      return
    }
    if (!hasMenuActions) {
      debouncedDownload.callback(file)
    } else {
      handleOnClickDisplayImage(file)
    }
  }, [loading, hasMenuActions, debouncedDownload, file, handleOnClickDisplayImage])

  const menuItems: MenuItemModel[] = [
    {
      label: t('dashboard.documents.showDocument'),
      onClick: () => {
        handleImageSrc(file, Number(installationId))
      },
    },
    {
      label: t('dashboard.documents.downloadDocument'),
      onClick: () => {
        if (installationId && file.id) downloadFile(Number(installationId), file)
      },
    },
  ]

  return (
    <Grid item xs={12} sm={6} md={4} lg={3} key={file.id} sx={{ px: 2, py: 1 }}>
      <FileButton
        name={file.name ?? ''}
        ext={file.extension ?? ''}
        key={file.id}
        onClickDownload={handleOnClickDownload}
        menuItems={hasMenuActions ? menuItems : undefined}
        actionIcon={hasMenuActions ? <E3more /> : undefined}
        depth={1}
        imageUrl={file.imageUrl}
        size={file.size?.toString()}
        fileClassName={fileClassName}
        apiEndpoint={process.env.REACT_APP_API_ENDPOINT ?? ''}
        mimeType={(file.mimeType as MimeType) ?? DEFAULT_MIME_TYPE}
        fullWidth={!isBigScreen}
        contentClassName={fileContentClassName}
        isWebview={isWebview}
      />
    </Grid>
  )
}

export default DocumentFile
